import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CardsSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import { formatAmountMinor, resolveFinanceLocale } from './finance-format';
import { isNullableNumber, isNullableString, isRecord } from './finance-view-guards';
import { useFinanceView } from './useFinanceView';

type CommessaAmounts = { grossMinor: number; netMinor: number };
type CommessaProgress = { gross: number | null; net: number | null };

type CommessaMilestone = {
  cashDate: string | null;
  description: string;
  counterparty: string | null;
  grossMinor: number;
  netMinor: number;
  taxMinor: number;
  settled: boolean;
};

type CommessaRow = {
  ref: string;
  label: string;
  milestoneCount: number;
  plan: CommessaAmounts;
  collected: CommessaAmounts;
  outstanding: CommessaAmounts;
  vatMinor: number;
  progressBasisPoints: CommessaProgress;
  nextCollectionOn: string | null;
  chargedCostMinor: number;
  milestones: CommessaMilestone[];
};

type CommessaLedger = {
  organizationId: string;
  rows: CommessaRow[];
  totals: Omit<CommessaRow, 'ref' | 'label' | 'nextCollectionOn' | 'milestones'>;
};

type Basis = 'gross' | 'net';

function isAmounts(value: unknown): value is CommessaAmounts {
  return (
    isRecord(value) && typeof value.grossMinor === 'number' && typeof value.netMinor === 'number'
  );
}

function isProgress(value: unknown): value is CommessaProgress {
  return isRecord(value) && isNullableNumber(value.gross) && isNullableNumber(value.net);
}

function hasAmountFigures(value: Record<string, unknown>): boolean {
  return (
    isAmounts(value.plan) &&
    isAmounts(value.collected) &&
    isAmounts(value.outstanding) &&
    typeof value.vatMinor === 'number' &&
    typeof value.chargedCostMinor === 'number' &&
    typeof value.milestoneCount === 'number' &&
    isProgress(value.progressBasisPoints)
  );
}

function isCommessaRow(value: unknown): value is CommessaRow {
  return (
    isRecord(value) &&
    typeof value.ref === 'string' &&
    typeof value.label === 'string' &&
    isNullableString(value.nextCollectionOn) &&
    Array.isArray(value.milestones) &&
    value.milestones.every(
      (milestone) =>
        isRecord(milestone) &&
        isNullableString(milestone.cashDate) &&
        typeof milestone.description === 'string' &&
        isNullableString(milestone.counterparty) &&
        typeof milestone.grossMinor === 'number' &&
        typeof milestone.netMinor === 'number' &&
        typeof milestone.taxMinor === 'number' &&
        typeof milestone.settled === 'boolean',
    ) &&
    hasAmountFigures(value)
  );
}

function isCommessaLedger(value: unknown): value is CommessaLedger {
  return (
    isRecord(value) &&
    typeof value.organizationId === 'string' &&
    Array.isArray(value.rows) &&
    value.rows.every(isCommessaRow) &&
    isRecord(value.totals) &&
    hasAmountFigures(value.totals)
  );
}

function pick(amounts: CommessaAmounts, basis: Basis): number {
  return basis === 'gross' ? amounts.grossMinor : amounts.netMinor;
}

type Formatters = {
  readonly amount: (valueMinor: number) => string;
  readonly percent: (basisPoints: number | null) => string;
};

function formatters(locale: ReturnType<typeof resolveFinanceLocale>): Formatters {
  const amount = (valueMinor: number) => formatAmountMinor(valueMinor, locale);
  const percent = (basisPoints: number | null) =>
    basisPoints === null
      ? '—'
      : new Intl.NumberFormat(locale === 'it' ? 'it-IT' : 'en-US', {
          style: 'percent',
          maximumFractionDigits: 1,
        }).format(basisPoints / 10_000);
  return { amount, percent };
}

function CommesseHead({ basis }: { readonly basis: Basis }) {
  const { t } = useTranslation('finance');
  return (
    <thead>
      <tr className="border-border border-b text-left">
        <th scope="col" className="py-2 font-medium">
          {t('commesse.columns.project')}
        </th>
        <th scope="col" className="py-2 pl-3 text-right font-medium">
          {t('commesse.columns.milestones')}
        </th>
        <th scope="col" className="py-2 pl-3 text-right font-medium">
          {t('commesse.columns.plan')}
        </th>
        <th scope="col" className="py-2 pl-3 text-right font-medium">
          {t('commesse.columns.collected')}
        </th>
        <th scope="col" className="py-2 pl-3 text-right font-medium">
          {t('commesse.columns.outstanding')}
        </th>
        <th scope="col" className="py-2 pl-3 text-right font-medium">
          {t('commesse.columns.progress')}
        </th>
        {basis === 'gross' && (
          <th scope="col" className="py-2 pl-3 text-right font-medium">
            {t('commesse.columns.vat')}
          </th>
        )}
        <th scope="col" className="py-2 pl-3 text-right font-medium">
          {t('commesse.columns.nextCollection')}
        </th>
        <th scope="col" className="py-2 pl-3 text-right font-medium">
          {t('commesse.columns.chargedCost')}
        </th>
      </tr>
    </thead>
  );
}

function CommessaRows({
  rows,
  basis,
  expanded,
  fmt,
  onToggle,
}: {
  readonly rows: CommessaLedger['rows'];
  readonly basis: Basis;
  readonly expanded: string | null;
  readonly fmt: Formatters;
  readonly onToggle: (ref: string) => void;
}) {
  const { amount, percent } = fmt;
  return (
    <>
      {rows.map((row) => (
        <tr key={row.ref} className="border-border/50 border-b">
          <th scope="row" className="py-1.5 text-left font-normal">
            <button
              type="button"
              className="text-left underline-offset-2 hover:underline"
              aria-expanded={expanded === row.ref}
              onClick={() => onToggle(row.ref)}
            >
              {row.label}
            </button>
            <span className="text-foreground/50 block text-xs">{row.ref}</span>
          </th>
          <td className="py-1.5 pl-3 text-right tabular-nums">{row.milestoneCount}</td>
          <td className="py-1.5 pl-3 text-right tabular-nums">{amount(pick(row.plan, basis))}</td>
          <td className="py-1.5 pl-3 text-right tabular-nums">
            {amount(pick(row.collected, basis))}
          </td>
          <td className="py-1.5 pl-3 text-right tabular-nums">
            {amount(pick(row.outstanding, basis))}
          </td>
          <td className="py-1.5 pl-3 text-right tabular-nums">
            {percent(row.progressBasisPoints[basis])}
          </td>
          {basis === 'gross' && (
            <td className="py-1.5 pl-3 text-right tabular-nums">{amount(row.vatMinor)}</td>
          )}
          <td className="py-1.5 pl-3 text-right tabular-nums">{row.nextCollectionOn ?? '—'}</td>
          <td className="py-1.5 pl-3 text-right tabular-nums">{amount(row.chargedCostMinor)}</td>
        </tr>
      ))}
    </>
  );
}

function TotalsRow({
  totals,
  basis,
  fmt,
}: {
  readonly totals: CommessaLedger['totals'];
  readonly basis: Basis;
  readonly fmt: Formatters;
}) {
  const { t } = useTranslation('finance');
  const { amount, percent } = fmt;
  return (
    <tr className="border-border border-t font-semibold">
      <th scope="row" className="py-1.5 text-left font-inherit">
        {t('commesse.totalRow')}
      </th>
      <td className="py-1.5 pl-3 text-right tabular-nums">{totals.milestoneCount}</td>
      <td className="py-1.5 pl-3 text-right tabular-nums">{amount(pick(totals.plan, basis))}</td>
      <td className="py-1.5 pl-3 text-right tabular-nums">
        {amount(pick(totals.collected, basis))}
      </td>
      <td className="py-1.5 pl-3 text-right tabular-nums">
        {amount(pick(totals.outstanding, basis))}
      </td>
      <td className="py-1.5 pl-3 text-right tabular-nums">
        {percent(totals.progressBasisPoints[basis])}
      </td>
      {basis === 'gross' && (
        <td className="py-1.5 pl-3 text-right tabular-nums">{amount(totals.vatMinor)}</td>
      )}
      <td className="py-1.5 pl-3" />
      <td className="py-1.5 pl-3 text-right tabular-nums">{amount(totals.chargedCostMinor)}</td>
    </tr>
  );
}

function MilestonesPanel({
  rows,
  expanded,
  basis,
  fmt,
}: {
  readonly rows: CommessaLedger['rows'];
  readonly expanded: string;
  readonly basis: Basis;
  readonly fmt: Formatters;
}) {
  const { t } = useTranslation('finance');
  const { amount } = fmt;
  return (
    <div className="border-border mt-4 rounded-md border p-3">
      <h2 className="text-sm font-medium">
        {t('commesse.milestonesTitle', {
          project: rows.find((row) => row.ref === expanded)?.label ?? expanded,
        })}
      </h2>
      <ul className="mt-2 space-y-1 text-xs">
        {(rows.find((row) => row.ref === expanded)?.milestones ?? []).map((milestone, index) => (
          <li key={`${milestone.cashDate ?? 'no-date'}-${index}`}>
            {milestone.cashDate ?? t('commesse.noDate')} · {milestone.description} ·{' '}
            {amount(basis === 'gross' ? milestone.grossMinor : milestone.netMinor)} ·{' '}
            {t(milestone.settled ? 'commesse.settled' : 'commesse.pending')}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FinanceCommessePage() {
  const { t, i18n } = useTranslation('finance');
  const locale = resolveFinanceLocale(i18n.resolvedLanguage);
  /*
   * Gross by default, which is what the source opens on. The two bases are not a rescaling of one
   * another - a job mixing rates has two different progress percentages - so the choice is offered
   * rather than resolved, and the server ships both so switching needs no second request.
   */
  const [basis, setBasis] = useState<Basis>('gross');
  const [expanded, setExpanded] = useState<string | null>(null);

  const request = useCallback(() => apiClient.GET('/v1/finance/commesse', {}), []);
  const view = useFinanceView(request, isCommessaLedger);

  if (view.status === 'loading') {
    return <CardsSkeleton />;
  }

  if (view.status === 'error') {
    return (
      <div className="p-6">
        <p className="text-sm">{t('commesse.loadError')}</p>
        <Button className="mt-3" onClick={view.reload}>
          {t('dashboard.retry')}
        </Button>
      </div>
    );
  }

  const { rows, totals } = view.data;
  const fmt = formatters(locale);

  return (
    <section className="p-6">
      <h2 className="screen-title">{t('commesse.title')}</h2>
      <p className="text-foreground/60 mt-1 text-sm">{t('commesse.subtitle')}</p>

      <div className="mt-4 flex gap-2" role="group" aria-label={t('commesse.basisSelector')}>
        {(['gross', 'net'] as const).map((candidate) => (
          <Button
            key={candidate}
            variant={candidate === basis ? 'default' : 'outline'}
            aria-pressed={candidate === basis}
            onClick={() => setBasis(candidate)}
          >
            {t(`commesse.basis.${candidate}`)}
          </Button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="mt-6 text-sm">{t('commesse.empty')}</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-3xl text-sm">
            <caption className="sr-only">{t('commesse.tableCaption')}</caption>
            <CommesseHead basis={basis} />
            <tbody>
              <CommessaRows
                {...{ rows, basis, expanded, fmt }}
                onToggle={(ref) => setExpanded(expanded === ref ? null : ref)}
              />
              <TotalsRow {...{ totals, basis, fmt }} />
            </tbody>
          </table>

          {expanded !== null && <MilestonesPanel {...{ rows, expanded, basis, fmt }} />}
        </div>
      )}

      <p className="text-foreground/60 mt-4 text-xs">{t('commesse.chargedCostNote')}</p>
    </section>
  );
}
